import React from "react";

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const MAX_VISIBLE_PAGES = 5;

    const handlePreviousPage = () => {
        if (currentPage > 1) onPageChange(currentPage - 1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) onPageChange(currentPage + 1);
    };

    const handlePageClick = (pageNumber) => {
        onPageChange(pageNumber);
    };

    const generatePageNumbers = () => {
        if (totalPages <= MAX_VISIBLE_PAGES) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);

        return [
            1,
            ...(start > 2 ? ['ellipsis-left'] : []),
            ...Array.from({ length: end - start + 1 }, (_, i) => start + i),
            ...(end < totalPages - 1 ? ['ellipsis-right'] : []),
            totalPages,
        ];
    };

    if (totalPages < 1) return null;

    return (
        <nav className="flex items-center justify-center sm:justify-end py-4" aria-label="Pagination">
            <ul className="inline-flex items-center -space-x-px rtl:space-x-retext-sm">
                {/* Previous Button */}
                <li>
                    <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                        className={`p-2 sm:px-4 sm:py-2 mx-1 transition-colors duration-200 
              rounded-lg border border-zinc-300 hover:bg-zinc-100 
              ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}
              text-sm sm:text-base`}
                    >
                        <span className="sr-only sm:not-sr-only">Previous</span>
                        <span className="sm:hidden">←</span>
                    </button>
                </li>

                {/* Page Numbers */}
                {generatePageNumbers().map((page, index) => {
                    if (page === 'ellipsis-left' || page === 'ellipsis-right') {
                        return (
                            <li key={`ellipsis-${index}`} className="px-2 py-2 text-zinc-500">
                                <span className="relative top-1">…</span>
                            </li>
                        );
                    }

                    const isActive = page === currentPage;

                    return (
                        <li key={page}>
                            <button
                                onClick={() => handlePageClick(page)}
                                aria-current={isActive ? "page" : undefined}
                                className={`hidden sm:block px-3 py-2 mx-1 transition-colors duration-200 
                  rounded-lg border ${isActive
                                        ? 'bg-blue-100 text-[#134F98] border-[#134F98]'
                                        : 'border-zinc-300 hover:bg-zinc-100 text-zinc-700'}
                  text-sm sm:text-base min-w-[2.5rem]`}
                            >
                                {page}
                            </button>
                        </li>
                    );
                })}

                {/* Next Button */}
                <li>
                    <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                        className={`p-2 sm:px-4 sm:py-2 mx-1 transition-colors duration-200 
              rounded-lg border border-zinc-300 hover:bg-zinc-100 
              ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}
              text-sm sm:text-base`}
                    >
                        <span className="sr-only sm:not-sr-only">Next</span>
                        <span className="sm:hidden">→</span>
                    </button>
                </li>
            </ul>

            {/* Mobile Current Page Display */}
            <div className="sm:hidden mx-4 text-sm text-zinc-600">
                Page {currentPage} of {totalPages}
            </div>
        </nav>
    );
};

export default Pagination;